import { TextInput } from '../../../components/FormUtils/TextInput'
import { useAuth } from 'context/authentication/useAuth'
import { useForm } from 'react-hook-form'
import to from 'await-to-js'
import { handleFormError } from '../../../utils/handleFormError'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorText } from '../../../components/FormUtils/ErrorText'
import styled from 'styled-components/macro'
import FbButton from '../../../components/FbUI/FbButton'
import { Checkbox } from 'components/FormUtils/Checkbox'
import { FbLink } from 'components/FbUI/FbLink'
import { HiOutlineMail } from 'react-icons/hi'

const registerFormShape = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  first_name: z.string().min(1, { message: 'First name is required' }),
  last_name: z.string().min(1, { message: 'Last name is required' }),
  password1: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters' }),
  password2: z.string(),
  non_field_errors: z.string().optional(),
  terms: z.boolean().refine((v) => v, {
    message: 'You must agree to the terms and conditions',
  }),
})

function Register() {
  const { createUser, isVerifyStep } = useAuth()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      password1: '',
      password2: '',
      non_field_errors: '',
      terms: false,
    },
    mode: 'onSubmit',
    resolver: zodResolver(registerFormShape),
  })

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(
        createUser(
          values.email,
          values.first_name,
          values.last_name,
          values.password1,
          values.password2
        )
      )
      handleFormError(err, setError)
    },
    (err) => console.warn(err)
  )

  // Success state - verification email sent
  if (isVerifyStep) {
    const firstName = getValues('first_name')

    return (
      <div className="w-96 flex-shrink-0">
        <div className="text-[22px] font-light text-new-company-gray-500">
          Welcome to{' '}
          <span className="text-new-company-green-dark font-semibold">
            First Bite
          </span>
        </div>

        <div className="mt-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-new-company-pistachio-light flex items-center justify-center mb-4">
            <HiOutlineMail className="w-8 h-8 text-new-company-green-dark" />
          </div>

          <h1 className="text-[20px] font-semibold mb-2">
            {firstName ? `Thanks, ${firstName}!` : 'Thanks for registering!'}
          </h1>

          <p className="text-gray-600 mb-6">
            We&apos;ve sent a verification email to your inbox. Please click the
            link in the email to verify your account before logging in.
          </p>

          <div className="w-full p-4 bg-gray-50 rounded-lg mb-6">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Tip:</span> If you don&apos;t see
              the email, check your spam or junk folder.
            </p>
          </div>

          <FbLink to="/login" className="text-center">
            Go to login
          </FbLink>
        </div>
      </div>
    )
  }

  // Form state - registration form
  return (
    <form onSubmit={onSubmit} className="w-96 flex-shrink-0">
      <div className="text-[22px] font-light text-new-company-gray-500">
        Welcome to{' '}
        <span className="text-new-company-green-dark font-semibold">
          First Bite
        </span>
      </div>

      <h1 className="text-[18px] mb-2 mt-4">Get Started</h1>
      <div className="mb-4">
        {'Already have an account? '}
        <FbLink to="/login">Log In</FbLink>
      </div>
      <div className="flex justify-between w-full gap-3">
        <TextInput control={control} name="first_name" label="First Name" />
        <TextInput control={control} name="last_name" label="Last Name" />
      </div>
      <TextInput control={control} name="email" label="Email" />
      <div className="text-gray-500 text-[12px]">
        Join your team by using your work email.
      </div>
      <TextInput
        type="password"
        control={control}
        name="password1"
        label="Password"
        helperText="Must be at least 8 characters"
      />
      <TextInput
        type="password"
        control={control}
        name="password2"
        label="Confirm Password"
      />

      <CheckboxWrapper>
        <Checkbox control={control} name="terms">
          <span className="text-[#475467]">
            By continuing, you agree to First Bite&apos;s{' '}
            <FbLink to="/terms-of-use">terms of use</FbLink> and{' '}
            <FbLink to="/privacy-policy">privacy policy</FbLink>
          </span>
        </Checkbox>
      </CheckboxWrapper>
      <FbButton
        className="w-full mt-4"
        type="submit"
        onClick={onSubmit}
        loading={isSubmitting}
      >
        Create Account
      </FbButton>
      <ErrorText control={control} name={'non_field_errors'} />
    </form>
  )
}

// Override base checkbox alignment
const CheckboxWrapper = styled.div`
  margin-top: 12px;
  .base-checkbox-container {
    align-items: flex-start !important;
  }
  .base-checkbox-container .custom-checkbox {
    margin-top: 2px;
  }
`

export default Register
